import { TableEvent } from '@baemingo/table-events';
import { ReportEvent } from '@baemingo/report-events';
import { OrderStatusEvent } from '@baemingo/order-status-events';
import { uniq, remove } from 'lodash';
import { v4 as uuid } from 'uuid';
import * as actions from './actions';
import _ from 'lodash';
import {
  LocalItem,
  CartItem,
  Languages,
  POSItemsIdsState,
  Station,
  User,
  Zone,
  Optional,
  CountryCode,
} from '~Types';
import {
  ORDER_STATUS,
  ORDER_ORIGIN,
  PAYMENT_STATUS,
  DISCOUNT_TYPE,
  SERVICE_TYPE,
} from '~../__queries__/globalTypes';
import { getExpressMenus_allAppliedMenus_menu } from '~Components/MenuManager/__queries__/getExpressMenus';
import {
  getCompany_Venue_company_predefinedReasons,
  getCompany_Venue_company_staffs,
} from '~Scenes/Venue/__queries__/getCompany';
import { InventoryEvent } from '@baemingo/inventory-events';
import { SerializedInput as SerializedTableStorage } from '@baemingo/table-events/dist/types';
import { SerializedStorage as SerializedInventoryStorage } from '@baemingo/inventory-events/dist/types';
import { SerializedStorage as SerializedReportStorage } from '@baemingo/report-events/dist/types';
import { SerializedStorage as SerializedCashStorage } from '@baemingo/cash-events/dist/types';
import { CashEvent } from '@baemingo/cash-events';
import { SerializedStorage as SerializedOrderStatusStorage } from '@baemingo/order-status-events/dist/types';
import {
  DeviceSettingsEvent,
  SerializedState as SerializedDeviceSettingsStorage,
} from '@baemingo/device-settings-events/dist/types';
import { PrintRow, PrintType } from '~Components/PrinterManager';

export const generateID = () => uuid();

type AuthToken = null | string;
interface ToggleMainMenu {
  type: typeof actions.TOGGLE_MAIN_MENU;
  isShow: boolean;
}

interface SetToken {
  type: typeof actions.SET_TOKEN;
  authToken: AuthToken;
}

type MainMenuAction = ToggleMainMenu | SetToken;

export function mainMenu(state = false, action: MainMenuAction): boolean {
  switch (action.type) {
    case actions.TOGGLE_MAIN_MENU:
      return action.isShow;
    case actions.SET_TOKEN:
      return false;
    default:
      return state;
  }
}

interface MessageVolumeChange {
  type: typeof actions.MESSAGE_VOLUME_CHANGE;
  volume: number;
}

interface NewIncomingOrder {
  type: typeof actions.NEW_INCOMING_ORDER;
}

type TimeOfLastActionAction = any;

export function timeOfLastAction(state: Date = new Date(), action: TimeOfLastActionAction): Date {
  // Dont wake the screen when a new order appears
  switch (action.type) {
    case actions.POS_PAYMENT_FAILED:
    case actions.POS_PAYMENT_COMPLETED:
    case actions.USER_PROVIDED_INPUT:
      return new Date();
    default:
      return state;
  }
}

interface MessageVersion {
  type: typeof actions.MESSAGE_VERSION;
  version: string;
}

type AppVersionAction = MessageVersion;

export function appVersion(state = 'N/A', action: AppVersionAction): string {
  switch (action.type) {
    case actions.MESSAGE_VERSION:
      return action.version;
    default:
      return state;
  }
}

interface UserProvidedInput {
  type: typeof actions.USER_PROVIDED_INPUT;
}
type HistoryAction = UserProvidedInput;
type HistoryState = any[];
export function history(state = [], action: HistoryAction): HistoryState {
  switch (action.type) {
    case actions.USER_PROVIDED_INPUT:
      return state;
    default:
      return [action, ...state.slice(0, 8)];
  }
}

type TimeOfLastOrderAction = NewIncomingOrder;

export function timeOfLastOrder(state = new Date(), action: TimeOfLastOrderAction): Date {
  switch (action.type) {
    case actions.NEW_INCOMING_ORDER:
      return new Date();
    default:
      return state;
  }
}

export type PosMode = 'SINGLE' | 'MULTI';

type SetLastUsedPosMode = {
  type: typeof actions.SET_LAST_USED_POS_MODE;
  mode: PosMode;
};

export function lastUsedPosMode(state: PosMode = 'SINGLE', action: SetLastUsedPosMode): PosMode {
  switch (action.type) {
    case actions.SET_LAST_USED_POS_MODE:
      return action.mode;

    default:
      return state;
  }
}

interface MessageAuthToken {
  type: typeof actions.MESSAGE_AUTH_TOKEN;
  authToken: AuthToken;
}
type AuthTokenAction = MessageAuthToken | SetToken;

export function authToken(
  state = localStorage[`TOKEN-${process.env.PROJECT_ID}-OM-v2`] || null,
  action: AuthTokenAction,
): AuthToken {
  switch (action.type) {
    case actions.MESSAGE_AUTH_TOKEN:
    case actions.SET_TOKEN:
      return action.authToken;
    default:
      return state;
  }
}

interface SetDailyTotal {
  type: typeof actions.SET_DAILY_TOTAL;
  dailyTotal: number;
}
type DailyTotalAction = SetDailyTotal;

export function dailyTotal(state = 0, action: DailyTotalAction): number {
  switch (action.type) {
    case actions.SET_DAILY_TOTAL:
      return action.dailyTotal;
    default:
      return state;
  }
}

interface SetOrdersCount {
  type: typeof actions.SET_ORDERS_COUNT;
  count: number;
}
type OrdersCountAction = SetOrdersCount;

export function ordersCount(state = 0, action: OrdersCountAction): number {
  switch (action.type) {
    case actions.SET_ORDERS_COUNT:
      return action.count;
    default:
      return state;
  }
}

type Volume = null | number;
type VolumeAction = MessageVolumeChange;

export function volume(state = null, action: VolumeAction): Volume {
  switch (action.type) {
    case actions.MESSAGE_VOLUME_CHANGE:
      return action.volume;
    default:
      return state;
  }
}

type DeviceId = null | string;
interface MessageDeviceId {
  type: typeof actions.MESSAGE_DEVICE_ID;
  deviceId: DeviceId;
}
type DeviceIdAction = MessageDeviceId;

export function deviceId(state = null, action: DeviceIdAction): DeviceId {
  switch (action.type) {
    case actions.MESSAGE_DEVICE_ID:
      return action.deviceId;
    default:
      return state;
  }
}

type DeviceOs = null | string;
interface MessageDeviceOs {
  type: typeof actions.MESSAGE_DEVICE_OS;
  deviceOS: DeviceOs;
}
type DeviceOSAction = MessageDeviceOs;

export function deviceOS(state = null, action: DeviceOSAction): DeviceId {
  switch (action.type) {
    case actions.MESSAGE_DEVICE_OS:
      return action.deviceOS;
    default:
      return state;
  }
}

type Locale = 'se' | 'en' | 'no' | 'es' | 'dk' | 'tr';
interface ChangeLocale {
  type: typeof actions.CHANGE_LOCALE;
  locale: Locale;
}
type LocaleAction = ChangeLocale;
export function locale(state: Locale = actions.DEFAULT_LOCALE, action: LocaleAction): Locale {
  switch (action.type) {
    case actions.CHANGE_LOCALE:
      return action.locale;
    default:
      return state;
  }
}
interface ChangeLanguage {
  type: typeof actions.CHANGE_LANGUAGE;
  language: Languages;
}
type LanguageAction = ChangeLanguage;

export function language(
  state: Languages = actions.DEFAULT_LOCALE,
  action: LanguageAction,
): Languages {
  switch (action.type) {
    case actions.CHANGE_LANGUAGE:
      return action.language;
    default:
      return state;
  }
}

export type Printer = {
  id: string;
  model: string;
  online: boolean;
  createdAt: string;
  primary: boolean;
  lastPrinted: string | null;
  coverOpen: boolean;
  paperEmpty: boolean;
  paperNearEmpty: boolean;
  stations: string[];
};

type PrintersState = Array<Printer>;
interface MessagePrinters {
  type: typeof actions.MESSAGE_PRINTERS;
  printers: PrintersState;
}
interface AddVirtualTestPrinter {
  type: typeof actions.ADD_VIRTUAL_TEST_PRINTER;
  printer: Printer;
}

type PrintersAction = MessagePrinters | AddVirtualTestPrinter;
const DEFAULT_PRINTERS: PrintersState = [];
export function printers(
  state: PrintersState = DEFAULT_PRINTERS,
  action: PrintersAction,
): PrintersState {
  switch (action.type) {
    case actions.MESSAGE_PRINTERS:
      return action.printers;
    case actions.ADD_VIRTUAL_TEST_PRINTER:
      return [...state, action.printer];
    default:
      return state;
  }
}

type RefundBridgeRequestsState = Array<{
  orderId: string;
  reference: string;
  amount: number;
  error: string | null;
  status: 'BOOT' | 'PENDING' | 'FAILED' | 'SUCCESS';
}>;
interface RefundBridgeRequest {
  type: typeof actions.REFUND_BRIDGE_REQUEST;
  reference: string;
  amount: number;
  orderId: string;
}
interface RefundBridgeRequestSent {
  type: typeof actions.REFUND_BRIDGE_REQUEST_SENT;
  reference: string;
}
interface MessageRefundBridgeRequestFailed {
  type: typeof actions.MESSAGE_REFUND_BRIDGE_REQUEST_FAILED;
  reference: string;
  error: string | null;
}
interface MessageRefundBridgeRequestSuccess {
  type: typeof actions.MESSAGE_REFUND_BRIDGE_REQUEST_SUCCESS;
  reference: string;
}

type RefundBridgeRequestsAction =
  | RefundBridgeRequest
  | RefundBridgeRequestSent
  | MessageRefundBridgeRequestFailed
  | MessageRefundBridgeRequestSuccess;

export function refundBridgeRequests(
  state: RefundBridgeRequestsState = [],
  action: RefundBridgeRequestsAction,
): RefundBridgeRequestsState {
  switch (action.type) {
    case actions.REFUND_BRIDGE_REQUEST:
      return [
        {
          orderId: action.orderId,
          reference: action.reference,
          amount: action.amount,
          error: null,
          status: 'BOOT',
        },
        ...state,
      ];
    case actions.REFUND_BRIDGE_REQUEST_SENT:
      return state.map((req) => {
        if (req.reference === action.reference) {
          return {
            ...req,
            status: 'PENDING',
          };
        }

        return req;
      });
    case actions.MESSAGE_REFUND_BRIDGE_REQUEST_FAILED:
      return state.map((req) => {
        if (req.reference === action.reference) {
          return {
            ...req,
            status: 'FAILED',
            error: action.error,
          };
        }

        return req;
      });
    case actions.MESSAGE_REFUND_BRIDGE_REQUEST_SUCCESS:
      return state.map((req) => {
        if (req.reference === action.reference) {
          return {
            ...req,
            status: 'SUCCESS',
          };
        }

        return req;
      });
    default:
      return state;
  }
}

type PaymentBridgeRequestsState = Array<{
  id: string;
  paymentId: string;
  amount: number;
  tips?: number;
  cardDetails?: CardDetails;
  method: string;
  error: string | null;
  status: 'BOOT' | 'PENDING' | 'FAILED' | 'SUCCESS';
}>;
interface PaymentBridgeRequest {
  type: typeof actions.PAYMENT_BRIDGE_REQUEST;
  id: string;
  amount: number;
  method: string;
}
interface PaymentBridgeRequestSent {
  type: typeof actions.PAYMENT_BRIDGE_REQUEST_SENT;
  id: string;
}
interface MessagePaymentBridgeRequestFailed {
  type: typeof actions.MESSAGE_PAYMENT_BRIDGE_REQUEST_FAILED;
  id: string;
  error: null | string;
}

interface PaymentResponse extends CardDetails {
  gratuityAmount?: number;
}

interface MessagePaymentBridgeRequestSuccess {
  type: typeof actions.MESSAGE_PAYMENT_BRIDGE_REQUEST_SUCCESS;
  paymentResponse?: PaymentResponse;
  amount: number;
  tips: number;
  id: string;
}

type PaymentBridgeRequestsAction =
  | PaymentBridgeRequest
  | PaymentBridgeRequestSent
  | MessagePaymentBridgeRequestFailed
  | MessagePaymentBridgeRequestSuccess;

export function paymentBridgeRequests(
  state: PaymentBridgeRequestsState = [],
  action: PaymentBridgeRequestsAction,
): PaymentBridgeRequestsState {
  switch (action.type) {
    case actions.PAYMENT_BRIDGE_REQUEST:
      return [
        {
          id: generateID(),
          paymentId: action.id,
          amount: action.amount,
          method: action.method,
          error: null,
          status: 'BOOT',
        },
        ...state,
      ];
    case actions.PAYMENT_BRIDGE_REQUEST_SENT:
      return state.map((req) => {
        if (req.id === action.id) {
          return {
            ...req,
            status: 'PENDING',
          };
        }

        return req;
      });
    case actions.MESSAGE_PAYMENT_BRIDGE_REQUEST_FAILED:
      return state.map((req) => {
        if (req.id === action.id) {
          return {
            ...req,
            status: 'FAILED',
            error: action.error,
          };
        }

        return req;
      });
    case actions.MESSAGE_PAYMENT_BRIDGE_REQUEST_SUCCESS:
      return state.map((req) => {
        if (req.id === action.id) {
          delete action.paymentResponse?.gratuityAmount;
          return {
            ...req,
            tips: action.tips,
            cardDetails: action.paymentResponse,
            status: 'SUCCESS',
          };
        }

        return req;
      });
    default:
      return state;
  }
}

interface MessagePrinterMessage {
  type: typeof actions.MESSAGE_PRINTER_MESSAGE;
  // the MESSAGE_PRINTER_MESSAGE is not used anywhere, so the message type is any
  message: any;
}
type PrinterMessagesAction = MessagePrinterMessage;

export function printerMessages(state = [], action: PrinterMessagesAction): any {
  switch (action.type) {
    case actions.MESSAGE_PRINTER_MESSAGE:
      return [action.message, ...state].slice(0, -500);
    default:
      return state;
  }
}

interface MessageAvailablePrinters {
  type: typeof actions.MESSAGE_AVAILABLE_PRINTERS;
  printers: AvailablePrinter[];
}
type AvailablePrintersAction = MessageAvailablePrinters;

export type AvailablePrinter = {
  id: string;
  type: string;
  model: string;
};

export function availablePrinters(
  state: AvailablePrinter[] = [],
  action: AvailablePrintersAction,
): AvailablePrinter[] {
  switch (action.type) {
    case actions.MESSAGE_AVAILABLE_PRINTERS:
      return action.printers;
    default:
      return state;
  }
}

interface SetOrderDetailsId {
  type: typeof actions.SET_ORDER_DETAILS_ID;
  orderDetailsId: string | null;
}
type OrderDetailsIdAction = SetOrderDetailsId;

export function orderDetailsId(state = null, action: OrderDetailsIdAction): string | null {
  switch (action.type) {
    case actions.SET_ORDER_DETAILS_ID:
      return action.orderDetailsId;
    default:
      return state;
  }
}

type CollapsedTablesState = string[];
interface SetCollapsedTable {
  type: typeof actions.SET_COLLAPSED_TABLE;
  sessionId: string;
}
interface UnsetCollapsedTable {
  type: typeof actions.UNSET_COLLAPSED_TABLE;
  sessionId: string;
}
type CollapsedTablesAction = SetCollapsedTable | UnsetCollapsedTable;

export function collapsedTables(
  state: CollapsedTablesState = [],
  action: CollapsedTablesAction,
): CollapsedTablesState {
  switch (action.type) {
    case actions.SET_COLLAPSED_TABLE:
      return uniq([...state, action.sessionId]);
    case actions.UNSET_COLLAPSED_TABLE:
      return remove(state, (id) => id !== action.sessionId);
    default:
      return state;
  }
}

export type StaffIDState = {
  id: string;
  code: string;
  name: string;
} | null;
interface PosStaffLogin {
  type: typeof actions.POS_STAFF_LOGIN;
  id: string;
  code: string;
  name: string;
}
interface PosStaffLogout {
  type: typeof actions.POS_STAFF_LOGOUT;
}
type StaffAction = PosStaffLogin | PosStaffLogout;

export function staff(state: StaffIDState = null, action: StaffAction): StaffIDState {
  switch (action.type) {
    case actions.POS_STAFF_LOGIN:
      return {
        id: action.id,
        code: action.code,
        name: action.name,
      };
    case actions.POS_STAFF_LOGOUT:
      return null;
    default:
      return state;
  }
}

type Staffs = getCompany_Venue_company_staffs[];
type CompanyStaffState = {
  companyId: string;
  staffs: Staffs;
} | null;
interface SetCompanyStaff {
  type: typeof actions.SET_COMPANY_STAFF;
  companyId: string;
  staffs: Staffs;
}
interface ResetCompanyStaff {
  type: typeof actions.RESET_COMPANY_STAFF;
}
type companyStaffAction = SetCompanyStaff | ResetCompanyStaff;

export function companyStaff(
  state: CompanyStaffState = null,
  action: companyStaffAction,
): CompanyStaffState {
  switch (action.type) {
    case actions.SET_COMPANY_STAFF:
      return {
        companyId: action.companyId,
        staffs: action.staffs,
      };
    case actions.RESET_COMPANY_STAFF:
      return null;
    default:
      return state;
  }
}

type POSLogsState = Array<{
  tableId: string;
  createdAt: Date;
  message: string;
}>;
interface SendTableItems {
  type: typeof actions.SEND_TABLE_ITEMS;
}
interface TablePaid {
  type: typeof actions.TABLE_PAID;
  id: string;
}
interface OpenTable {
  type: typeof actions.OPEN_TABLE;
}
interface SendTableItemsPosLogs extends SendTableItems {
  id: string;
}
interface OpenTablePOSLogs extends OpenTable {
  id: string;
}
type PosLogsAction = TablePaid | OpenTablePOSLogs | SendTableItemsPosLogs;

export function POSLogs(state: POSLogsState = [], action: PosLogsAction): POSLogsState {
  switch (action.type) {
    case actions.TABLE_PAID: {
      return [
        {
          tableId: action.id,
          createdAt: new Date(),
          message: 'Table paid',
        },
        ...state,
      ];
    }

    case actions.OPEN_TABLE: {
      return [
        {
          tableId: action.id,
          createdAt: new Date(),
          message: 'Table opened',
        },
        ...state,
      ];
    }

    case actions.SEND_TABLE_ITEMS: {
      return [
        {
          tableId: action.id,
          createdAt: new Date(),
          message: 'Table bong',
        },
        ...state,
      ];
    }

    default:
      return state;
  }
}

interface MessageReceiptCounter {
  type: typeof actions.MESSAGE_RECEIPT_COUNTER;
  receiptCounter: number;
}
interface PrintReceipt {
  type: typeof actions.PRINT_RECEIPT;
}

interface IncreaseReceiptCounter {
  type: typeof actions.INCREASE_RECEIPT_COUNTER;
}

type POSReceiptCounterAction = MessageReceiptCounter | PrintReceipt | IncreaseReceiptCounter;

export function POSReceiptCounter(state = 0, action: POSReceiptCounterAction): number {
  switch (action.type) {
    case actions.MESSAGE_RECEIPT_COUNTER:
      return action.receiptCounter;
    case actions.PRINT_RECEIPT:
    case actions.INCREASE_RECEIPT_COUNTER:
      // if (action.isInvoicePayment) {
      //   return state;
      // }

      window.postMessageBridge({ receiptCounter: state + 1 });
      return state + 1;
    default:
      return state;
  }
}

type POSLayoutState = string | null | 'NONE';
interface MessagePosLayout {
  type: typeof actions.MESSAGE_POS_LAYOUT;
  devicePOSLayout: string;
}
interface SetPosLayout {
  type: typeof actions.SET_POS_LAYOUT;
  POSLayout: string;
}
type POSLayoutAction = MessagePosLayout | SetPosLayout;

export function POSLayout(
  state: POSLayoutState = 'clnuhsynj0sj30c279louggss',
  action: POSLayoutAction,
): POSLayoutState {
  switch (action.type) {
    case actions.MESSAGE_POS_LAYOUT:
      return action.devicePOSLayout;
    case actions.SET_POS_LAYOUT:
      return action.POSLayout;
    default:
      return state;
  }
}

type POSLayoutsState = string[];
interface MessagePosLayouts {
  type: typeof actions.MESSAGE_POS_LAYOUT;
  POSLayouts: POSLayoutsState;
}
interface SetPosLayouts {
  type: typeof actions.SET_POS_LAYOUTS;
  POSLayouts: POSLayoutsState;
}
type POSLayoutsAction = MessagePosLayouts | SetPosLayouts;

export function POSLayouts(
  state: POSLayoutsState = [],
  action: POSLayoutsAction,
): POSLayoutsState {
  switch (action.type) {
    case actions.MESSAGE_POS_LAYOUT:
      return action.POSLayouts;
    case actions.SET_POS_LAYOUTS:
      return action.POSLayouts;
    default:
      return state;
  }
}

type StationsState = Station[] | null;
interface SetStations {
  type: typeof actions.SET_STATIONS;
  stations: Station[];
}
type StationsAction = SetStations;
export function stations(state: StationsState = null, action: StationsAction): StationsState {
  switch (action.type) {
    case actions.SET_STATIONS:
      return action.stations;
    default:
      return state;
  }
}

type StationState = string | null;
interface SetStation {
  type: typeof actions.SET_STATION;
  id: string;
}
interface ResetStation {
  type: typeof actions.RESET_STATION;
}
type StationAction = SetStation | ResetStation;

export function station(state: StationState = null, action: StationAction): StationState {
  switch (action.type) {
    case actions.SET_STATION:
      return action.id;
    case actions.RESET_STATION:
      return null;
    default:
      return state;
  }
}

type ZonesState = Zone[];
interface SetZones {
  type: typeof actions.SET_ZONES;
  zones: Zone[];
}
type ZonesAction = SetZones;
export function zones(state: ZonesState = [], action: ZonesAction): ZonesState {
  switch (action.type) {
    case actions.SET_ZONES:
      return action.zones;
    default:
      return state;
  }
}

type BongPrintingHaltedState = boolean;
interface HaltBongPrinting {
  type: typeof actions.HALT_BONG_PRINTING;
}
interface ResumeBongPrinting {
  type: typeof actions.RESUME_BONG_PRINTING;
}
type BongPrintingHaltedAction = HaltBongPrinting | ResumeBongPrinting;

export function BongPrintingHalted(
  state: BongPrintingHaltedState = false,
  action: BongPrintingHaltedAction,
): BongPrintingHaltedState {
  switch (action.type) {
    case actions.HALT_BONG_PRINTING:
      return true;
    case actions.RESUME_BONG_PRINTING:
      return false;
    default:
      return state;
  }
}
type Order = {
  id: string;
  realId?: string;
  comment?: string;
  chequeName?: string;
  orderNumber?: string;
  desiredTime?: string;
  estimatedFinishTime?: string;
  deliveryLocation?: string;
  staffId?: string;
  coverage?: number;
  tableId?: string;
};
type POSBongGroupsState = Array<{
  id: string;
  name?: string | null;
  serviceType?: string;
  items: LocalItem[];
  messages?: LocalItem[];
  tableId: string | null;
  order?: Order;
  origin?: ORDER_ORIGIN | null;
  onlyPhysical?: boolean;
  ticketMessages?: TicketMessage[];
  skipConfirmation?: boolean;
}>;

type TicketMessage = {
  id: string;
  message: string;
  courseId?: string;
};

interface SendOrder {
  type: typeof actions.SEND_ORDER;
  name?: string;
  order: Order;
  serviceType?: string;
  items: LocalItem[];
  messages?: LocalItem[];
  tableId?: string;
  origin?: ORDER_ORIGIN | null;
  ticketMessages?: TicketMessage[];
}

interface SendOnlineOrder {
  type: typeof actions.SEND_ONLINE_ORDER;
  order: Order;
  serviceType?: string;
  items: LocalItem[];
  messages?: LocalItem[];
  tableId: string | null;
  origin?: ORDER_ORIGIN;
}

interface PrintPhysicalBong {
  type: typeof actions.PRINT_PHYSICAL_BONG;
  order: Order;
  serviceType: string;
  items: LocalItem[];
  origin: ORDER_ORIGIN;
}

interface BongGroupBonged {
  type: typeof actions.BONG_GROUP_BONGED;
  id: string;
}
interface SendTableItemsPOSBongGroups extends SendTableItems {
  order?: Order;
  id?: string;
  items: CartItem[] | LocalItem[];
  serviceType?: SERVICE_TYPE;
  // for the SEND_TABLE_ITEMS action, the name field is unknown
  name?: string;
  messages?: any[];
  origin?: ORDER_ORIGIN | null;
  skipConfirmation?: boolean;
}
type POSBongGroupsAction =
  | SendTableItemsPOSBongGroups
  | SendOnlineOrder
  | SendOrder
  | PrintPhysicalBong
  | BongGroupBonged;

export function POSBongGroups(
  state: POSBongGroupsState = [],
  action: POSBongGroupsAction,
): POSBongGroupsState {
  switch (action.type) {
    case actions.PRINT_PHYSICAL_BONG:
      return [
        ...state,
        {
          id: generateID(),
          items: action.items,
          order: action.order,
          serviceType: action.serviceType,
          tableId: null,
          origin: action.origin,
          onlyPhysical: true,
        },
      ];
    case actions.SEND_TABLE_ITEMS:
      return [
        ...state,
        {
          id: generateID(),
          name: action.name,
          items: action.items,
          order: action.order,
          serviceType: action.serviceType,
          tableId: action.id || null,
          messages: action.messages,
          origin: action.origin,
          skipConfirmation: action.skipConfirmation,
        },
      ];
    case actions.SEND_ORDER:
      return [
        ...state,
        {
          id: generateID(),
          name: action.name,
          items: action.items,
          order: action.order,
          serviceType: action.serviceType || 'Take away',
          tableId: action.order.chequeName || null,
          messages: action.messages,
          origin: action.origin,
          ticketMessages: action.ticketMessages,
        },
      ];
    case actions.SEND_ONLINE_ORDER:
      return [
        ...state,
        {
          id: generateID(),
          items: action.items,
          order: action.order,
          serviceType: action.serviceType || 'Take away',
          tableId: action.tableId,
          messages: action.messages,
          origin: action.origin,
        },
      ];
    case actions.BONG_GROUP_BONGED:
      return state.filter((g) => g.id !== action.id);
    default:
      return state;
  }
}

type POSReceiptPrintsState = {
  QR?: string;
  id?: string;
  terminal?: string | null;
  payment?: POSPayment;
  order?: POSOrder;
} | null;

interface ClearReceipt {
  type: typeof actions.CLEAR_RECEIPT;
}
interface PrintReceipt {
  type: typeof actions.PRINT_RECEIPT;
  id: string;
  terminal?: string | null;
  QR?: string;
  isInvoicePayment?: boolean;
  payment?: POSPayment;
  order?: POSOrder;
}
type POSReceiptPrintsAction = PrintReceipt | ClearReceipt;

export function POSReceiptPrints(
  state: POSReceiptPrintsState = null,
  action: POSReceiptPrintsAction,
): POSReceiptPrintsState {
  switch (action.type) {
    case actions.PRINT_RECEIPT:
      return {
        id: action.id,
        terminal: action.terminal,
        QR: action.QR,
        payment: action.payment,
        order: action.order,
      };
    case actions.CLEAR_RECEIPT:
      return null;
    default:
      return state;
  }
}

export interface PrintInQueue {
  id: string;
  printer: string;
  printerModel: string;
  reprintOfPrintId?: string;
  orderId?: string;
  sessionId?: string;
  error?: string;
  success: boolean;
  reprintedOnPrimary?: boolean;
  isPrinting?: boolean;
  lastPrinted?: Date;
  data: string | PrintRow[];
  printType: PrintType;
  base64Image?: string;
}
export type POSPrintQueueState = Array<PrintInQueue>;
interface AddPrintToQueue {
  type: typeof actions.ADD_PRINT_TO_QUEUE;
  printer: string;
  printerModel: string;
  orderId?: string;
  sessionId?: string;
  qr?: string;
  data: string | PrintRow[];
  printType: PrintType;
  reprintOfPrintId?: string;
}
interface ChangePrintQueuePrinter {
  type: typeof actions.CHANGE_PRINT_QUEUE_PRINTER;
  id: string;
  printer: string;
}
interface PrintQueue {
  type: typeof actions.PRINT_QUEUE;
  id: string;
}
interface PrintQueueWithOrderId {
  type: typeof actions.PRINT_QUEUE_WITH_ORDER_ID;
  orderId: string;
}
interface RemovePrintFromQueue {
  type: typeof actions.REMOVE_PRINT_FROM_QUEUE;
  id: string;
}
interface MessagePrintBridgeRequestSuccess {
  type: typeof actions.MESSAGE_PRINT_BRIDGE_REQUEST_SUCCESS;
  id: string;
}
interface MessagePrintBridgeRequestFailed {
  type: typeof actions.MESSAGE_PRINT_BRIDGE_REQUEST_FAILED;
  id: string;
  error: string;
}

interface ReprintOnPrimary {
  type: typeof actions.REPRINT_ON_PRIMARY;
  id: string;
}

type POSPrintQueue =
  | AddPrintToQueue
  | ChangePrintQueuePrinter
  | PrintQueue
  | PrintQueueWithOrderId
  | RemovePrintFromQueue
  | MessagePrintBridgeRequestSuccess
  | MessagePrintBridgeRequestFailed
  | ReprintOnPrimary;
export function POSPrintQueue(
  state: POSPrintQueueState = [],
  action: POSPrintQueue,
): POSPrintQueueState {
  switch (action.type) {
    case actions.ADD_PRINT_TO_QUEUE:
      return [...state, { id: generateID(), success: false, ...action }];
    case actions.CHANGE_PRINT_QUEUE_PRINTER:
      return state.map((print) =>
        print.id === action.id
          ? {
              ...print,
              printer: action.printer,
            }
          : print,
      );
    case actions.PRINT_QUEUE:
      return state.map((print) =>
        print.id === action.id
          ? { ...print, isPrinting: true, error: undefined, lastPrinted: new Date() }
          : print,
      );
    case actions.PRINT_QUEUE_WITH_ORDER_ID:
      return state.map((print) =>
        print.orderId === action.orderId
          ? { ...print, isPrinting: true, error: undefined, lastPrinted: new Date() }
          : print,
      );
    case actions.REMOVE_PRINT_FROM_QUEUE:
      return state.filter(
        (print) =>
          !(
            print.id === action.id ||
            (print.reprintOfPrintId && print.reprintOfPrintId === action.id)
          ),
      );
    case actions.MESSAGE_PRINT_BRIDGE_REQUEST_SUCCESS:
      return state.map((print) =>
        print.id === action.id ? { ...print, success: true, isPrinting: false } : print,
      );
    case actions.MESSAGE_PRINT_BRIDGE_REQUEST_FAILED:
      return state.map((print) =>
        print.id === action.id
          ? { ...print, error: action.error || 'Unknown error', isPrinting: false }
          : print,
      );
    case actions.REPRINT_ON_PRIMARY:
      return state.map((print) =>
        print.id === action.id ? { ...print, reprintedOnPrimary: true } : print,
      );
    default:
      return state;
  }
}

export type PaymentMethod = {
  id: string;
  title: string;
  active: boolean;
  // for the ADD_PAYMENT_METHOD action, icon is not used in call action, so icon is optional
  icon?: string | null;

  /** If populated, this payment method is limited to specific AccessLevels */
  accessLevels: string[];

  /** If populated, this payment method is only available on certain venues */
  venues: string[];

  isPrimary?: boolean;
};

type POSPaymentMethodsState = Array<PaymentMethod>;
interface AddPaymentMethod {
  type: typeof actions.ADD_PAYMENT_METHOD;
  method: {
    id: string;
    title: string;
    active: boolean;
    accessLevels: string[];
    venues: string[];
    isPrimary?: boolean;
  };
}
type POSPaymentMethodsAction = AddPaymentMethod;

export function POSPaymentMethods(
  state: POSPaymentMethodsState = [],
  action: POSPaymentMethodsAction,
): POSPaymentMethodsState {
  switch (action.type) {
    case actions.ADD_PAYMENT_METHOD:
      return [...state, action.method].sort((a, b) => a.title.localeCompare(b.title));
    default:
      return state;
  }
}

export type CardDetails = {
  cardType?: string;
  cardPaymentEntryMode?: string;
  tsi?: string;
  tvr?: string;
  aid?: string;
  maskedPan?: string;
};

export interface Meta {
  paymentId?: string;
  transactionId?: string;
  serviceId?: string;
  amount?: number;
  tip?: number;
  invoiceTarget?: string;
  invoiceTargetName?: string | null;
  qr?: string | null;
  autocomplete?: boolean;
  cardDetails?: CardDetails;
  cancelRequested?: boolean;
  cancelled?: boolean;
  terminalStatus?: string;
}

export interface POSPayment {
  id: string;
  method: string;
  variant: string | null | undefined;
  order: string;
  canCancel: boolean;
  disableTip?: boolean;
  status: 'PENDING' | 'FAILED' | 'COMPLETED';
  meta: Meta;
  amount: number;
  // message field for action.error message
  message?: string;
  hasConnectionIssues?: boolean;
}
type POSPaymentsState = Array<POSPayment>;
interface POSPaymentRequest {
  type: typeof actions.POS_PAYMENT_REQUEST;
  id?: string;
  method: string;
  amount: number;
  disableTip?: boolean;
  variant?: string;
  order: string;
  canCancel?: boolean;
  meta?: object;
}
interface UpdatePOSPayment {
  type: typeof actions.UPDATE_POS_PAYMENT;
  payment: POSPayment;
}
interface POSPaymentMeta {
  type: typeof actions.POS_PAYMENT_META;
  id: string;
  meta: Meta;
}
interface POSPaymentFailed {
  type: typeof actions.POS_PAYMENT_FAILED;
  id: string;
  error?: string;
}
interface POSPaymentCompleted {
  type: typeof actions.POS_PAYMENT_COMPLETED;
  id: string;
  meta?: Partial<Meta>;
  paymentId?: string;
}
interface ClearOrder {
  type: typeof actions.CLEAR_ORDER;
  id: string;
}
interface RemovePOSOrder {
  type: typeof actions.REMOVE_POS_PAYMENT;
  id: string;
}
interface SyncPOSOrder {
  type: typeof actions.POS_ORDER_SYNCED;
  id: string;
}
interface OverridePOSPaymentMethod {
  type: typeof actions.OVERRIDE_POS_PAYMENT_METHOD;
  method: string;
  paymentId: string;
}
type POSPaymentsAction =
  | POSPaymentRequest
  | UpdatePOSPayment
  | POSPaymentMeta
  | POSPaymentFailed
  | POSPaymentCompleted
  | ClearOrder
  | SyncPOSOrder
  | OverridePOSPaymentMethod
  | RemovePOSOrder;

export function POSPayments(
  state: POSPaymentsState = [],
  action: POSPaymentsAction,
): POSPaymentsState {
  switch (action.type) {
    case actions.POS_PAYMENT_REQUEST:
      return [
        ...state,
        {
          id: action.id || generateID(),
          method: action.method,
          variant: action.variant,
          order: action.order,
          amount: action.amount,
          canCancel: action.canCancel || false,
          status: 'PENDING',
          meta: action.meta || {},
        },
      ];
    case actions.UPDATE_POS_PAYMENT:
      return state.map((payment) => {
        if (payment.id === action.payment.id) {
          return {
            ...payment,
            ...action.payment,
          };
        }

        return payment;
      });
    case actions.POS_PAYMENT_META:
      return state.map((payment) => {
        if (payment.id === action.id) {
          return {
            ...payment,
            meta: {
              ...payment.meta,
              ...action.meta,
            },
          };
        }

        return payment;
      });
    case actions.POS_PAYMENT_FAILED:
      return state.map((payment) => {
        if (payment.id === action.id) {
          return {
            ...payment,
            status: 'FAILED',
            message: action.error,
          };
        }

        return payment;
      });
    case actions.POS_PAYMENT_COMPLETED:
      return state.map((payment) => {
        if (payment.id === action.id) {
          return {
            ...payment,
            status: 'COMPLETED',
            message: 'Payment Completed',
            meta: {
              ...payment.meta,
              ...action.meta,
            },
            paymentId: action.paymentId,
          };
        }

        return payment;
      });

    case actions.CLEAR_ORDER:
    case actions.POS_ORDER_SYNCED:
      return state.filter((payment) => payment.order !== action.id);
    case actions.REMOVE_POS_PAYMENT:
      return state.filter((payment) => payment.id !== action.id);
    case actions.OVERRIDE_POS_PAYMENT_METHOD:
      return state.map((payment) => {
        if (payment.id === action.paymentId) {
          return {
            ...payment,
            method: action.method,
          };
        }

        return payment;
      });
    default:
      return state;
  }
}

type DiscountType = DISCOUNT_TYPE | null;
export interface POSOrder {
  id: string;
  createdAt: Date;
  desiredTime?: string;
  items: CartItem[];
  parent?: string;

  // Used when setting a specific staff ID
  // that might be different from currently logged in staff
  staffId?: string;
  chequeId: string | null;
  discountType?: DiscountType;
  discountRate?: number | null;
  serviceType: SERVICE_TYPE;
  method?: string;
  defaultOrderStatus?: ORDER_STATUS;
  noReceipt?: boolean;
  orderNumber?: string;
  origin?: ORDER_ORIGIN;
  expanded?: boolean;
  manualSync?: boolean;
  subPayments?: {
    transactionId: string;
    type: string;
    status: PAYMENT_STATUS;
    amount: number;
    tip: number;
  }[];
  zoneId?: string | null;
  bonged?: boolean;
  comment: string;
  userId?: string | null;
  bookingId?: string | null;
}
type POSOrdersState = Array<POSOrder>;

export interface CreatePOSOrderSubPayments {
  transactionId: string;
  type: string;
  status: PAYMENT_STATUS;
  amount: number;
  tip: number;
}
export interface CreatePOSOrder {
  type: typeof actions.CREATE_POS_ORDER;
  id?: string;
  chequeId?: string;
  method?: string;
  desiredTime?: string;
  discountType?: DiscountType;
  discountRate?: number | null;
  serviceType?: SERVICE_TYPE;
  defaultOrderStatus?: ORDER_STATUS;
  items?: CartItem[];
  noReceipt?: boolean;
  orderNumber?: string;
  origin?: ORDER_ORIGIN;
  staffId?: string;
  manualSync?: boolean;
  subPayments?: CreatePOSOrderSubPayments[];
  zoneId?: string | null;
  bonged?: boolean;
  comment?: string;
  userId?: string | null;
  bookingId?: string | null;
}
interface UpdatePOSOrder {
  type: typeof actions.UPDATE_POS_ORDER;
  id: string;
  // in the action UPDATE_POS_ORDER order fields (like discount or expanded) not found in CreatePOSOrder
  order: any;
}
interface JoinPOSOrder {
  type: typeof actions.JOIN_POS_ORDER;
  id: string;
}
interface AddItemToPOSOrder {
  type: typeof actions.ADD_ITEM_TO_POS_ORDER;
  id: string;
  item: Optional<CartItem, 'id'>;
}
interface RemoveItemFromPOSOrder {
  type: typeof actions.REMOVE_ITEM_FROM_POS_ORDER;
  orderId: string;
  id: string;
}
interface RemoveLegalItemsFromPOSOrder {
  type: typeof actions.REMOVE_LEGAL_ITEMS_FROM_POS_ORDER;
  orderId: string;
}
interface ClearOrder {
  type: typeof actions.CLEAR_ORDER;
  id: string;
}
interface RemovePosOrder {
  type: typeof actions.REMOVE_POS_ORDER;
  id: string;
}
type POSOrdersAction =
  | CreatePOSOrder
  | UpdatePOSOrder
  | JoinPOSOrder
  | AddItemToPOSOrder
  | RemoveItemFromPOSOrder
  | RemoveLegalItemsFromPOSOrder
  | ClearOrder
  | RemovePosOrder;

export function POSOrders(state: POSOrdersState = [], action: POSOrdersAction): POSOrdersState {
  switch (action.type) {
    case actions.CREATE_POS_ORDER:
      return [
        ...state,
        {
          id: action.id || generateID(),
          createdAt: new Date(),
          desiredTime: action.desiredTime,
          items: action.items || [],
          chequeId: action.chequeId ?? null,
          discountType: action.discountType,
          discountRate: action.discountRate,
          serviceType: action.serviceType || SERVICE_TYPE.EAT_HERE,
          method: action.method,
          manualSync: action.manualSync,
          defaultOrderStatus: action.defaultOrderStatus || ORDER_STATUS.COMPLETED,
          noReceipt: action.noReceipt,
          // If set, prints orderNumber instead of Bong
          orderNumber: action.orderNumber,
          origin: action.origin,
          staffId: action.staffId,
          subPayments: action.subPayments ?? [],
          zoneId: action.zoneId,
          bonged: action.bonged,
          comment: action.comment || '',
          userId: action.userId || null,
          bookingId: action.bookingId || null,
        },
      ];
    case actions.UPDATE_POS_ORDER:
      return state.map((order) => {
        if (order.id === action.id) {
          return {
            ...order,
            ...action.order,
          };
        }

        return order;
      });
    case actions.JOIN_POS_ORDER:
      return state.reduce((arr, order) => {
        if (order.id === action.id) {
          const parent = arr.find((o) => o.id === order.parent)!;

          parent.items = parent.items.concat(order.items);
        } else {
          arr.push(order);
        }

        return arr;
      }, [] as POSOrdersState);

    case actions.ADD_ITEM_TO_POS_ORDER: {
      return <Array<POSOrder>>state.map((order) => {
        if (order.id === action.id) {
          return {
            ...order,
            items: [
              ...order.items,
              {
                ...action.item,
                id: action.item.id || generateID(),
              },
            ],
          };
        }

        return order;
      });
    }
    case actions.REMOVE_ITEM_FROM_POS_ORDER: {
      return state.map((order) => {
        if (order.id === action.orderId) {
          return {
            ...order,
            items: [...order.items.filter((item) => item.id !== action.id)],
          };
        }

        return order;
      });
    }
    case actions.REMOVE_LEGAL_ITEMS_FROM_POS_ORDER: {
      return state.map((order) => {
        if (order.id === action.orderId) {
          return {
            ...order,
            items: [...order.items.filter((item) => !item.ageRestricted)],
          };
        }

        return order;
      });
    }
    case actions.CLEAR_ORDER:
    case actions.REMOVE_POS_ORDER:
      return state.filter((order) => order.id !== action.id);
    default:
      return state;
  }
}

export type Payment = {
  id: string;
  amount: number;
  method: string;
  methodTitle: string;
  refunded?: boolean;
  meta: {
    paymentId?: string;
    amount?: number;
    invoiceTarget?: string;
    transactionId?: string;
  };
};
type POSSplitOrderState = {
  order: string;
  finished?: boolean;
  payments: Array<Payment>;
} | null;
interface CreateSplitPOSOrder {
  type: typeof actions.CREATE_SPLIT_POS_ORDER;
  order: string;
}
interface AddPaymentToSplitOrder {
  type: typeof actions.ADD_PAYMENT_TO_SPLIT_ORDER;
  payment: Payment;
}
interface RemovePaymentFromSplitOrder {
  type: typeof actions.REMOVE_PAYMENT_FROM_SPLIT_ORDER;
  payment: string;
}
interface POSPaymentMeta {
  type: typeof actions.POS_PAYMENT_META;
}
interface RefundSplitPayment {
  payment: string;
  type: typeof actions.REFUND_SPLIT_PAYMENT;
}
interface FinishSplitPOSOrder {
  type: typeof actions.FINISH_SPLIT_POS_ORDER;
}
interface ClearSplitPOSOrder {
  type: typeof actions.CLEAR_SPLIT_POS_ORDER;
}
type POSSplitOrderAction =
  | CreateSplitPOSOrder
  | AddPaymentToSplitOrder
  | RemovePaymentFromSplitOrder
  | POSPaymentMeta
  | RefundSplitPayment
  | FinishSplitPOSOrder
  | ClearSplitPOSOrder;

export function POSSplitOrder(
  state: POSSplitOrderState = null,
  action: POSSplitOrderAction,
): POSSplitOrderState {
  switch (action.type) {
    case actions.CREATE_SPLIT_POS_ORDER:
      return {
        order: action.order,
        finished: false,
        payments: [],
      };
    case actions.ADD_PAYMENT_TO_SPLIT_ORDER:
      return {
        ...state!,
        payments: [
          ...state!.payments,
          {
            ...action.payment,
          },
        ],
      };
    case actions.REMOVE_PAYMENT_FROM_SPLIT_ORDER:
      return {
        ...state!,
        payments: state!.payments.filter((payment) => payment.id !== action.payment),
      };
    case actions.POS_PAYMENT_META:
      return state
        ? {
            ...state,
            payments: state.payments.map((payment) => {
              if (payment.id === action.id) {
                return {
                  ...payment,
                  meta: {
                    ...(payment.meta ?? {}),
                    ...action.meta,
                  },
                };
              }
              return payment;
            }),
          }
        : null;
    case actions.REFUND_SPLIT_PAYMENT:
      return {
        ...state!,
        payments: state!.payments.map((payment) => {
          if (payment.id === action.payment) {
            return {
              ...payment,
              refunded: true,
            };
          }
          return payment;
        }),
      };
    case actions.FINISH_SPLIT_POS_ORDER:
      return {
        ...state!,
        finished: true,
      };
    case actions.CLEAR_SPLIT_POS_ORDER:
      return null;
    default:
      return state;
  }
}

type POSOrdersForSyncState = Array<{
  order: POSOrder;
  amount: number;
  payment: POSPayment;
}>;
interface SyncPosOrder {
  type: typeof actions.SYNC_POS_ORDER;
  order: POSOrder;
  amount: number;
  payment: POSPayment;
}
interface PosOrderSynced {
  type: typeof actions.POS_ORDER_SYNCED;
  id: string;
}
type POSOrdersForSync = SyncPosOrder | PosOrderSynced;
export function POSOrdersForSync(
  state: POSOrdersForSyncState = [],
  action: POSOrdersForSync,
): POSOrdersForSyncState {
  switch (action.type) {
    case actions.SYNC_POS_ORDER:
      return [...state, action];
    case actions.POS_ORDER_SYNCED:
      return state.filter((sync) => sync.order.id !== action.id);
    default:
      return state;
  }
}

type POSMenuState = getExpressMenus_allAppliedMenus_menu | null | false;
interface SetPOSMenu {
  type: typeof actions.SET_POS_MENU;
  menu: getExpressMenus_allAppliedMenus_menu;
}
type POSMenuAction = SetPOSMenu;
export function POSMenu(state: POSMenuState = null, action: POSMenuAction): POSMenuState {
  switch (action.type) {
    case actions.SET_POS_MENU:
      return action.menu;
    default:
      return state;
  }
}

type POSTakeAwayMenuState = getExpressMenus_allAppliedMenus_menu | null | false;
interface SetPOSTakeAwayMenu {
  type: typeof actions.SET_POS_TAKE_AWAY_MENU;
  menu: getExpressMenus_allAppliedMenus_menu;
}
type POSTakeAwayMenuAction = SetPOSTakeAwayMenu;
export function POSTakeAwayMenu(
  state: POSTakeAwayMenuState = null,
  action: POSTakeAwayMenuAction,
): POSTakeAwayMenuState {
  switch (action.type) {
    case actions.SET_POS_TAKE_AWAY_MENU:
      return action.menu;
    default:
      return state;
  }
}

interface SetConnectionStatus {
  type: typeof actions.SET_CONNECTION_STATUS;
  offline: boolean;
}
type OfflineAction = SetConnectionStatus;
export function offline(state = false, action: OfflineAction): boolean {
  switch (action.type) {
    case actions.SET_CONNECTION_STATUS:
      return action.offline;
    default:
      return state;
  }
}

type UserState = User | null;
interface SetUser {
  type: typeof actions.SET_USER;
  user: User;
}
type UserAction = SetUser;
export function user(state: UserState = null, action: UserAction): UserState {
  switch (action.type) {
    case actions.SET_USER:
      return action.user;
    default:
      return state;
  }
}

export interface ErrorCase {
  /** A request to add this error */
  type: typeof actions.ERROR;
  /** If it's an order affected */
  orderId?: string;
  /** Type of error */
  errorType: 'SKV' | 'ORDER_SYNC';
  /** What happened? For instance: ApolloError: Failed to fetch */
  title: string;
  /** How do the customer solve it? Any actions? */
  message: string;
}
type ErrorState = Array<Omit<ErrorCase & { id: string; createdAt: Date }, 'type'>>;
interface ClearError {
  /** A request to clear a specific error */
  type: typeof actions.CLEAR_ERROR;
  id: string;
}
interface ClearAllError {
  /** A request to clear a all errors */
  type: typeof actions.CLEAR_ALL_ERRORS;
}
type ErrorsAction = ErrorCase | ClearError | ClearAllError;
export function errors(state: ErrorState = [], action: ErrorsAction): ErrorState {
  switch (action.type) {
    case actions.ERROR:
      return [
        {
          id: uuid(),
          createdAt: new Date(),
          orderId: action.orderId,
          errorType: action.errorType,
          title: action.title,
          message: action.message,
        },
        ...state,
      ];
    case actions.CLEAR_ERROR:
      return state.filter((e) => e.id !== action.id);
    case actions.CLEAR_ALL_ERRORS:
      return [];
    default:
      return state;
  }
}

type EJournalState = string[];
interface MessageJournal {
  type: typeof actions.MESSAGE_JOURNAL;
  lines: string[];
}
type EJournalAction = MessageJournal;
export function eJournal(state: EJournalState = [], action: EJournalAction): EJournalState {
  switch (action.type) {
    case actions.MESSAGE_JOURNAL:
      return action.lines;
    default:
      return state;
  }
}

type CompanyState = string | null;
interface SetCompany {
  type: typeof actions.SET_COMPANY;
  companyId: string;
}

interface SetCompanyName {
  type: typeof actions.SET_COMPANY_NAME;
  companyName: string;
}

type CompanyAction = SetCompany | SetCompanyName;

export function company(state: CompanyState = null, action: CompanyAction): CompanyState {
  switch (action.type) {
    case actions.SET_COMPANY:
      return action.companyId;
    default:
      return state;
  }
}

export function companyName(state = '', action: CompanyAction): string {
  switch (action.type) {
    case actions.SET_COMPANY_NAME:
      return action.companyName;
    default:
      return state;
  }
}

type VenueState = string | null;
interface SetVenueId {
  type: typeof actions.SET_VENUE_ID;
  venueId: string;
}

interface SetVenueName {
  type: typeof actions.SET_VENUE_NAME;
  venueName: string;
}

type VenueAction = SetVenueId;
export function venueId(state: VenueState = null, action: VenueAction): VenueState {
  switch (action.type) {
    case actions.SET_VENUE_ID:
      return action.venueId;
    default:
      return state;
  }
}

export function venueName(state = '', action: SetVenueName): string {
  switch (action.type) {
    case actions.SET_VENUE_NAME:
      return action.venueName;
    default:
      return state;
  }
}

type CompanyTimeZone = string | null;
interface SetCompanyTimeZone {
  type: typeof actions.SET_COMPANY_TIMEZONE;
  companyTimeZone: string;
}
type CompanyTimeZoneAction = SetCompanyTimeZone;
export function companyTimeZone(
  state: VenueState = null,
  action: CompanyTimeZoneAction,
): CompanyTimeZone {
  switch (action.type) {
    case actions.SET_COMPANY_TIMEZONE:
      return action.companyTimeZone;
    default:
      return state;
  }
}

type ActiveIntegrationsState = any[];
interface SetActiveIntegration {
  type: typeof actions.SET_ACTIVE_INTEGRATIONS;
  integrations: ActiveIntegrationsState;
}
type ActiveIntegrationsAction = SetActiveIntegration;
export function activeIntegrations(
  state: ActiveIntegrationsState = [],
  action: ActiveIntegrationsAction,
): ActiveIntegrationsState {
  switch (action.type) {
    case actions.SET_ACTIVE_INTEGRATIONS:
      return action.integrations;
    default:
      return state;
  }
}
interface SetPOSItemsIDs {
  type: typeof actions.SET_POS_ITEMS_IDS;
  itemIds: POSItemsIdsState;
}
type POSItemsIdsAction = SetPOSItemsIDs;
export function POSItemsIds(
  state: POSItemsIdsState = [],
  action: POSItemsIdsAction,
): POSItemsIdsState {
  switch (action.type) {
    case actions.SET_POS_ITEMS_IDS:
      return action.itemIds;
    default:
      return state;
  }
}

interface MessageIsDiscoveringPrinters {
  type: typeof actions.MESSAGE_IS_DISCOVERING_PRINTERS;
  isDiscovering: boolean;
}
type POSPrinterScanActiveAction = MessageIsDiscoveringPrinters;
export function POSPrinterScanActive(state = false, action: POSPrinterScanActiveAction): boolean {
  switch (action.type) {
    case actions.MESSAGE_IS_DISCOVERING_PRINTERS:
      return action.isDiscovering;
    default:
      return state;
  }
}

interface Module {
  id: string;
}

interface ModulesState {
  activeWebpage: string | null;
  modules: Module[];
}
interface SetModulesWebPage {
  type: typeof actions.SET_MODULES_WEBPAGE;
  url: string | null;
}
type ModulesAction = SetModulesWebPage;
export function modules(
  state: ModulesState = {
    activeWebpage: null,
    modules: [],
  },
  action: ModulesAction,
): ModulesState {
  switch (action.type) {
    case actions.SET_MODULES_WEBPAGE:
      return {
        ...state,
        activeWebpage: action.url,
      };
    default:
      return state;
  }
}

interface SetLastVisitedPath {
  type: typeof actions.SET_LAST_VISITED_PATH;
  path: string;
}
type LastVisitedPathAction = SetLastVisitedPath;

export function lastVisitedPath(
  state = localStorage.getItem(`PATH-${process.env.PROJECT_ID}-OM`) || null,
  action: LastVisitedPathAction,
) {
  switch (action.type) {
    case actions.SET_LAST_VISITED_PATH:
      localStorage.setItem(`PATH-${process.env.PROJECT_ID}-OM`, action.path);
      return action.path;
    default:
      return state;
  }
}

type AdyenTerminal = {
  IP: string;
  POIID: string;
  active?: boolean;
};
interface SetAdyenTerminals {
  type: typeof actions.SET_ADYEN_TERMINALS;
  data: AdyenTerminal[];
}
type AdyenTerminalsAction = SetAdyenTerminals;
export function AdyenTerminals(state = [], action: AdyenTerminalsAction) {
  const localState = localStorage.getItem(`ADYEN_TERMINALS-${process.env.PROJECT_ID}-OM`);
  switch (action.type) {
    case actions.SET_ADYEN_TERMINALS:
      localStorage.setItem(
        `ADYEN_TERMINALS-${process.env.PROJECT_ID}-OM`,
        JSON.stringify(action.data),
      );
      return action.data;
    default:
      return localState ? JSON.parse(localState) : state;
  }
}

interface AddTableEventAction {
  type: 'ADD_TABLE_EVENT';
  event: TableEvent;
}

interface AddTableEventsAction {
  type: 'ADD_TABLE_EVENTS';
  events: TableEvent[];
}

interface MarkTableEventSentAction {
  type: 'MARK_EVENTS_SENT';
  ids: string[];
  sentAt: string;
}

export function tableEvents(
  state: TableEvent[] = [],
  action: AddTableEventAction | MarkTableEventSentAction | AddTableEventsAction,
): TableEvent[] {
  switch (action.type) {
    case 'ADD_TABLE_EVENTS':
      return _.uniqBy([...state, ...action.events], 'eventId');
    case 'ADD_TABLE_EVENT':
      return _.uniqBy([...state, action.event], 'eventId');
    case 'MARK_EVENTS_SENT':
      return state.map((event) => {
        if (action.ids.includes(event.eventId)) {
          return {
            ...event,
            sentAt: action.sentAt,
          };
        }

        return event;
      });
    default:
      return state;
  }
}

/**
 * This state tracks when we last got data from the backend
 */
export function lastTableEventData(state: string | null = null, action: any) {
  switch (action.type) {
    case 'NEW_TABLE_DATA':
      return new Date().toISOString();
    default:
      return state;
  }
}

export interface TableSnapshotState {
  snapshot: SerializedTableStorage | null;
  loading: boolean;
  timestamp: string | null;
}

export function tableSnapshot(
  state: TableSnapshotState = {
    snapshot: null,
    loading: true,
    timestamp: null,
  },
  action: any,
) {
  switch (action.type) {
    case 'SET_TABLE_SNAPSHOT':
      return {
        snapshot: action.snapshot,
        timestamp: action.timestamp,
        loading: false,
      };
    default:
      return state;
  }
}

interface AddInventoryEventAction {
  type: 'ADD_INVENTORY_EVENT';
  event: InventoryEvent;
}

interface MarkTableEventSentAction {
  type: 'MARK_EVENTS_SENT';
  ids: string[];
  sentAt: string;
}

export function inventoryEvents(
  state: InventoryEvent[] = [],
  action: AddInventoryEventAction | MarkTableEventSentAction,
): InventoryEvent[] {
  switch (action.type) {
    case 'ADD_INVENTORY_EVENT':
      return [...state, action.event];
    case 'MARK_EVENTS_SENT':
      return state.map((event) => {
        if (action.ids.includes(event.eventId)) {
          return {
            ...event,
            sentAt: action.sentAt,
          };
        }

        return event;
      });
    default:
      return state;
  }
}

export interface InventorySnapshotState {
  snapshot: SerializedInventoryStorage | null;
  loading: boolean;
  timestamp: string | null;
}

export function inventorySnapshot(
  state: InventorySnapshotState = {
    snapshot: null,
    loading: true,
    timestamp: null,
  },
  action: any,
) {
  switch (action.type) {
    case 'SET_INVENTORY_SNAPSHOT':
      return {
        snapshot: action.snapshot,
        timestamp: action.timestamp,
        loading: false,
      };
    default:
      return state;
  }
}

interface AddReportEventAction {
  type: 'ADD_REPORT_EVENT';
  event: ReportEvent;
}

interface MarkTableEventSentAction {
  type: 'MARK_EVENTS_SENT';
  ids: string[];
  sentAt: string;
}

export function reportEvents(
  state: ReportEvent[] = [],
  action: AddReportEventAction | MarkTableEventSentAction,
): ReportEvent[] {
  switch (action.type) {
    case 'ADD_REPORT_EVENT':
      return [...state, action.event];
    case 'MARK_EVENTS_SENT':
      return state.map((event) => {
        if (action.ids.includes(event.eventId)) {
          return {
            ...event,
            sentAt: action.sentAt,
          };
        }

        return event;
      });
    default:
      return state;
  }
}

export interface ReportSnapshotState {
  snapshot: SerializedReportStorage | null;
  loading: boolean;
  timestamp: string | null;
}

export function reportSnapshot(
  state: ReportSnapshotState = {
    snapshot: null,
    loading: true,
    timestamp: null,
  },
  action: any,
) {
  switch (action.type) {
    case 'SET_REPORT_SNAPSHOT':
      return {
        snapshot: action.snapshot,
        timestamp: action.timestamp,
        loading: false,
      };
    default:
      return state;
  }
}

export function POSReport(state = null, action: any) {
  switch (action.type) {
    case 'MESSAGE_INIT_REPORT':
      return action.zReport;
    case 'MESSAGE_Z_REPORT':
      return action.zReport;
    default:
      return state;
  }
}
interface AddCashEventAction {
  type: 'ADD_CASH_EVENT';
  event: CashEvent;
}
interface MarkCashEventSentAction {
  type: 'MARK_EVENTS_SENT';
  ids: string[];
  sentAt: string;
}

export function cashEvents(
  state: CashEvent[] = [],
  action: AddCashEventAction | MarkCashEventSentAction,
): CashEvent[] {
  switch (action.type) {
    case 'ADD_CASH_EVENT':
      return [...state, action.event];
    case 'MARK_EVENTS_SENT':
      return state.map((event) => {
        if (action.ids.includes(event.eventId)) {
          return {
            ...event,
            sentAt: action.sentAt,
          };
        }

        return event;
      });
    default:
      return state;
  }
}

export interface CashSnapshotState {
  snapshot: SerializedCashStorage | null;
  loading: boolean;
  timestamp: string | null;
}

export function cashSnapshot(
  state: CashSnapshotState = {
    snapshot: null,
    loading: true,
    timestamp: null,
  },
  action: any,
) {
  switch (action.type) {
    case 'SET_CASH_SNAPSHOT':
      return {
        snapshot: action.snapshot,
        timestamp: action.timestamp,
        loading: false,
      };
    default:
      return state;
  }
}

interface AddDeviceSettingsEvents {
  type: 'ADD_DEVICE_SETTINGS_EVENTS';
  events: DeviceSettingsEvent[];
}
interface AddDeviceSettingsEvent {
  type: 'ADD_DEVICE_SETTINGS_EVENT';
  event: DeviceSettingsEvent;
}
interface MarkDeviceSettingsEventSentAction {
  type: 'MARK_EVENTS_SENT';
  ids: string[];
  sentAt: string;
}

export function deviceSettingsEvents(
  state: DeviceSettingsEvent[] = [],
  action: AddDeviceSettingsEvent | AddDeviceSettingsEvents | MarkDeviceSettingsEventSentAction,
): DeviceSettingsEvent[] {
  switch (action.type) {
    case 'ADD_DEVICE_SETTINGS_EVENTS':
      return [...state, ...action.events];
    case 'ADD_DEVICE_SETTINGS_EVENT':
      return [...state, action.event];
    case 'MARK_EVENTS_SENT':
      return state.map((event) => {
        if (action.ids.includes(event.eventId)) {
          return {
            ...event,
            sentAt: action.sentAt,
          };
        }

        return event;
      });
    default:
      return state;
  }
}

export interface DeviceSettingsSnapshotState {
  snapshot: SerializedDeviceSettingsStorage | null;
  loading: boolean;
  timestamp: string | null;
}

export function deviceSettingsSnapshot(
  state: DeviceSettingsSnapshotState = {
    snapshot: null,
    loading: true,
    timestamp: null,
  },
  action: any,
) {
  switch (action.type) {
    case 'SET_DEVICE_SETTINGS_SNAPSHOT':
      return {
        snapshot: action.snapshot,
        timestamp: action.timestamp,
        loading: false,
      };
    default:
      return state;
  }
}

interface AddOrderStatusEventAction {
  type: 'ADD_ORDER_STATUS_EVENT';
  event: OrderStatusEvent;
}

interface AddOrderStatusEventsAction {
  type: 'ADD_ORDER_STATUS_EVENTS';
  events: OrderStatusEvent[];
}

interface MarkOrderStatusEventSentAction {
  type: 'MARK_EVENTS_SENT';
  ids: string[];
  sentAt: string;
}

export function orderStatusEvents(
  state: OrderStatusEvent[] = [],
  action: AddOrderStatusEventAction | AddOrderStatusEventsAction | MarkOrderStatusEventSentAction,
): OrderStatusEvent[] {
  switch (action.type) {
    case 'ADD_ORDER_STATUS_EVENT':
      return _.uniqBy([...state, action.event], 'eventId');
    case 'ADD_ORDER_STATUS_EVENTS':
      return _.uniqBy([...state, ...action.events], 'eventId');
    case 'MARK_EVENTS_SENT':
      return state.map((event) => {
        if (action.ids.includes(event.eventId)) {
          return {
            ...event,
            sentAt: action.sentAt,
          };
        }

        return event;
      });
    default:
      return state;
  }
}

/**
 * This state tracks when we last got data from the backend
 */
export function lastOrderStatusData(state: string = new Date().toISOString(), action: any) {
  switch (action.type) {
    case 'NEW_ORDER_STATUS_DATA':
      return new Date().toISOString();
    default:
      return state;
  }
}

export interface OrderStatusSnapshotState {
  snapshot: SerializedOrderStatusStorage | null;
  loading: boolean;
  timestamp: string | null;
}

export function orderStatusSnapshot(
  state: OrderStatusSnapshotState = {
    snapshot: null,
    loading: true,
    timestamp: null,
  },
  action: any,
) {
  switch (action.type) {
    case 'SET_ORDER_STATUS_SNAPSHOT':
      return {
        snapshot: action.snapshot,
        timestamp: action.timestamp,
        loading: false,
      };
    default:
      return state;
  }
}

type Toast = {
  id: string;
  message: string;
  autoRemove: boolean;
  isError: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
};

export function toasts(state: Toast[] = [], action: any) {
  switch (action.type) {
    case 'ADD_TOAST':
      return [...state, action];
    case 'REMOVE_TOAST':
      return state.filter((toast) => toast.id !== action.id);
    default:
      return state;
  }
}

type CompanyFlags = {
  hasPOS: boolean;
  hasCDS: boolean;
  hasKDS: boolean;
  hasInventory: boolean;
  hasTables: boolean;
  hasAppOrders: boolean;
  hasPoints: boolean;
  hasExpress: boolean;
  manualEndOfDay: boolean;
  useNewLicenses: boolean;
  isPaymentsOnly: boolean;
};

export function companyFlags(
  state: CompanyFlags = {
    hasCDS: false,
    hasPOS: false,
    hasKDS: false,
    hasInventory: false,
    hasPoints: false,
    hasAppOrders: false,
    hasExpress: false,
    hasTables: false,
    manualEndOfDay: false,
    useNewLicenses: false,
    isPaymentsOnly: false,
  },
  action: any,
): CompanyFlags {
  switch (action.type) {
    case 'SET_COMPANY_FLAGS':
      return action.flags;
    default:
      return state;
  }
}

interface TraceIDAction {
  type: typeof actions.SET_TRACE_ID;
  id: string;
}

export function traceId(state = uuid(), action: TraceIDAction): string {
  switch (action.type) {
    case actions.SET_TRACE_ID:
      return action.id;
    default:
      return state;
  }
}

interface IntegrationsLoadingAction {
  type: typeof actions.SET_INTEGRATIONS_LOADING;
  loading: boolean;
}

export function integrationsLoading(state = true, action: IntegrationsLoadingAction): boolean {
  switch (action.type) {
    case actions.SET_INTEGRATIONS_LOADING:
      return action.loading;
    default:
      return state;
  }
}

export function demoMode(state = false, action: any): boolean {
  switch (action.type) {
    case 'SET_DEMO_MODE':
      return action.state;
    default:
      return state;
  }
}

export function country(state = null, action: any): CountryCode | null {
  switch (action.type) {
    case 'SET_COUNTRY':
      return action.state;
    default:
      return state;
  }
}

export function defaultLanguage(state = null, action: any): Languages | null {
  switch (action.type) {
    case 'SET_DEFAULT_LANGUAGE':
      return action.state;
    default:
      return state;
  }
}

type AdyenRequestState = {
  id: string;
  request: any;
}[];

export function adyenRequestQueue(state: AdyenRequestState = [], action: any): AdyenRequestState {
  switch (action.type) {
    case 'ADD_ADYEN_REQUEST':
      return [...state, action.request];
    case 'REMOVE_ADYEN_REQUESTS':
      return state.filter((request) => !action.ids.includes(request.id));
    default:
      return state;
  }
}

interface StartPaymentRequest {
  type: 'START_PAYMENT_REQUEST';
  serviceId: string;
  amount: number;
  transactionId: string;
  terminalId: string;
}

export function startPaymentRequestQueue(
  state: StartPaymentRequest[] = [],
  action: any,
): StartPaymentRequest[] {
  switch (action.type) {
    case 'START_PAYMENT_REQUEST':
      return [...state, action.payload];
    case 'REMOVE_START_PAYMENT_REQUEST':
      return state.filter((r) => r.serviceId !== action.serviceId);
    default:
      return state;
  }
}

export function beeperList(state = [], action: any): string[] {
  switch (action.type) {
    case 'SET_BEEPER_LIST':
      return action.state;
    default:
      return state;
  }
}

export function enableAutoCashEOD(state = false, action: any): boolean {
  switch (action.type) {
    case 'SET_ENABLE_AUTO_CASH_EOD':
      return action.state;
    default:
      return state;
  }
}

export interface BongPrint {
  id: string;
  printerId: string;
  hasBeenAttempted: boolean;
  /** Why did it print / not print */
  reason?: string;
}

export interface BongGroup {
  title: string;
  staffId?: string;
  timestamp: string;
  prints?: BongPrint[];
}

type BongGroupHistoryState = Record<string, BongGroup>;

interface SaveBongPrint {
  type: typeof actions.SAVE_BONG_PRINT;
  groupId: string;
  title: string;
  staffId?: string;
  print: BongPrint;
}

type BongGroupHistoryAction = SaveBongPrint;

/** History of why physical bongs printed or not. Visible in the UI in the PrinterStatus component */
export function BongGroupHistory(
  state: BongGroupHistoryState = {},
  action: BongGroupHistoryAction,
): BongGroupHistoryState {
  switch (action.type) {
    case actions.SAVE_BONG_PRINT: {
      const { groupId, title, staffId, print } = action;

      const newGroupEntry = {
        title,
        staffId,
        timestamp: state[groupId]?.timestamp || new Date().toISOString(),
        prints: [print, ...(state[groupId]?.prints || [])],
      };

      const newState: BongGroupHistoryState = {
        [groupId]: newGroupEntry,
        ..._.omit(state, groupId),
      };

      /** Keep a max of 50 entries for performance reasons */
      const limitedState = _.fromPairs(Object.entries(newState).slice(0, 50));

      return limitedState;
    }

    default:
      return state;
  }
}

export function predefinedReasons(
  state = [],
  action: any,
): getCompany_Venue_company_predefinedReasons[] {
  switch (action.type) {
    case 'SET_PREDEFINED_REASONS':
      return action.state;
    default:
      return state;
  }
}

export function bookingDiscountCategoryIds(state: string[] = [], action: any): string[] {
  switch (action.type) {
    case 'SET_BOOKING_DISCOUNT_CATEGORY_IDS':
      return action.state;
    default:
      return state;
  }
}

export interface DeviceWakeState {
  wakeTimestamp: string | null;
  hasSyncedTableEvents: boolean;
  syncingTableEvents: boolean;
}

export function deviceWakeState(
  state: DeviceWakeState = {
    wakeTimestamp: null,
    hasSyncedTableEvents: true,
    syncingTableEvents: false,
  },
  action: any,
): DeviceWakeState {
  switch (action.type) {
    case 'MESSAGE_DEVICE_WAKE':
      return {
        wakeTimestamp: new Date().toISOString(),
        hasSyncedTableEvents: false,
        syncingTableEvents: true,
      };
    case 'DEVICE_WAKE_TABLE_SYNC_COMPLETE':
      return {
        ...state,
        hasSyncedTableEvents: true,
        syncingTableEvents: false,
      };
    default:
      return state;
  }
}

export interface State {
  adyenRequestQueue: any[];
  startPaymentRequestQueue: StartPaymentRequest[];
  printerMessages: any[];
  availablePrinters: AvailablePrinter[];
  mainMenu: boolean;
  demoMode: boolean;
  BongGroupHistory: BongGroupHistoryState;
  tableEvents: TableEvent[];
  lastTableEventData: string | null;
  tableSnapshot: TableSnapshotState;
  inventoryEvents: InventoryEvent[];
  inventorySnapshot: InventorySnapshotState;
  reportEvents: ReportEvent[];
  reportSnapshot: ReportSnapshotState;
  cashEvents: CashEvent[];
  cashSnapshot: CashSnapshotState;
  deviceSettingsEvents: DeviceSettingsEvent[];
  deviceSettingsSnapshot: DeviceSettingsSnapshotState;
  orderStatusEvents: OrderStatusEvent[];
  orderStatusSnapshot: OrderStatusSnapshotState;
  lastOrderStatusData: string;
  errors: ErrorState;
  printers: PrintersState;
  companyFlags: CompanyFlags;
  POSReceiptPrints: POSReceiptPrintsState;
  POSOrders: POSOrdersState;
  POSMenu: POSMenuState;
  POSTakeAwayMenu: POSTakeAwayMenuState;
  POSPayments: POSPaymentsState;
  POSOrdersForSync: POSOrdersForSyncState;
  POSLogs: POSLogsState;
  POSPaymentMethods: POSPaymentMethodsState;
  POSReport: any | null;
  staff: StaffIDState;
  companyStaff: CompanyStaffState;
  locale: Locale;
  POSPrinterScanActive: boolean;
  POSSplitOrder: POSSplitOrderState;
  POSPrintQueue: POSPrintQueueState;
  timeOfLastAction: Date;
  POSBongGroups: POSBongGroupsState;
  refundBridgeRequests: RefundBridgeRequestsState;
  paymentBridgeRequests: PaymentBridgeRequestsState;
  BongPrintingHalted: BongPrintingHaltedState;
  POSReceiptCounter: number;
  POSLayout: POSLayoutState;
  POSLayouts: POSLayoutsState;
  modules: ModulesState;
  stations: StationsState;
  station: StationState;
  zones: ZonesState;
  eJournal: EJournalState;
  activeIntegrations: ActiveIntegrationsState;
  POSItemsIds: POSItemsIdsState;
  company: string;
  companyName: string;
  language: Languages;
  appVersion: string;
  deviceOS: string;
  deviceId: DeviceId;
  user: UserState;
  history: HistoryState;
  authToken: AuthToken;
  volume: Volume;
  offline: boolean;
  lastVisitedPath: string | null;
  AdyenTerminals: AdyenTerminal[];
  toasts: Toast[];
  venueId: VenueState;
  venueName: string;
  companyTimeZone: string;
  lastUsedPosMode: PosMode;
  integrationsLoading: boolean;
  country: CountryCode | null; // Is null if the company was created before this field was added
  defaultLanguage: Languages | null;
  beeperList: string[];
  enableAutoCashEOD: boolean; // Setting set in backend that (if enabled) automatically closes the cash for the day at the end of the day
  hasIndividualEOD: boolean; // Setting set in backend that (if enabled) allows individual EODs for each device
  predefinedReasons: getCompany_Venue_company_predefinedReasons[];
  bookingDiscountCategoryIds: string[];
  deviceWakeState: DeviceWakeState;
}
